/**
 * InspectPopoverCell — self-contained Popover trigger + `InspectPopover` content for
 * one non-SELECT-eligible availability-grid cell.
 *
 * Both `DetailAvailabilityCard`'s route and the offer-draft availability tab (task 4.4)
 * render non-SELECT-eligible cells via `AvailabilityGrid`'s `renderCellContent` slot, which
 * is independent of the grid's `multiSelect` click/drag handling — this lets a plain click
 * on an occupied cell open `InspectPopover` without ever entering the multi-select rectangle
 * path. The trigger's `onClick` stops propagation so the click does not also bubble into the
 * grid's own `<td>` click handler (relevant for SELECT-eligible-but-inspected states, e.g. an
 * own-held cell the offer-draft tab still routes to INSPECT — see that tab's wiring).
 */
import * as React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@ooh/ui/components/popover';
import { Button } from '@ooh/ui/components/button';
import { InspectPopover } from './inspect-popover';
import type { InspectPopoverProps } from './inspect-popover';
import { POPOVER_CONTENT_CLAMP_CLASS, POPOVER_CONTENT_WIDTH_CLASS } from './cell-popover-state';

export interface InspectPopoverCellProps extends InspectPopoverProps {
  trigger: React.ReactNode;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  triggerTestId: string;
}

export function InspectPopoverCell({
  trigger,
  isOpen,
  onOpenChange,
  triggerTestId,
  ...popoverProps
}: InspectPopoverCellProps) {
  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          data-testid={triggerTestId}
          className="flex h-full w-full items-center justify-center p-0 font-normal hover:bg-transparent"
          onClick={(event) => event.stopPropagation()}
        >
          {trigger}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        // Same overflow guard as `availability-rich-cell.tsx`'s INSPECT popover — the
        // queue-join panel's mandatory shared-expiry control (task 4.3,
        // `reservation-mixed-range-and-target-expiry`) can make this content taller
        // than the viewport, which Radix's collision shifting alone cannot fix.
        className={`${POPOVER_CONTENT_WIDTH_CLASS} p-0 ${POPOVER_CONTENT_CLAMP_CLASS}`}
        onClick={(event) => event.stopPropagation()}
        data-testid="inspect-popover-content"
      >
        <InspectPopover {...popoverProps} />
      </PopoverContent>
    </Popover>
  );
}
